import { useQuery } from '@tanstack/react-query';
import { tokenStorage } from '@/lib/token-storage';
import { decodeJwtPayload } from '@/lib/jwt';
import type { User } from '@/auth/types';

export const authKeys = {
  currentUser: ['auth', 'current-user'] as const,
};

/**
 * Decode the signed-in user from the Keycloak access-token claims. Internal
 * (realm `internal`) sessions carry `sub`/`email`/`name`/`realm_access.roles`,
 * so no extra server call is needed to resolve the base identity.
 */
export function userFromToken(): User | null {
  const token = tokenStorage.access;
  if (!token) return null;
  const p = decodeJwtPayload(token) as {
    sub?: string;
    email?: string;
    preferred_username?: string;
    name?: string;
    given_name?: string;
    family_name?: string;
    realm_access?: { roles?: string[] };
  };
  const uuid = p.sub ?? tokenStorage.uuid ?? '';
  if (!uuid) return null;
  return {
    uuid,
    email: p.email ?? p.preferred_username ?? '',
    name:
      p.name ?? ([p.given_name, p.family_name].filter(Boolean).join(' ') || undefined),
    roles: p.realm_access?.roles ?? [],
  };
}

/**
 * Current-user query. `enabled` gates it on the presence of a token so it stays
 * idle until login. Reads purely from the token today; swap the queryFn for a
 * `GET /me` call against dataCentralApi when a richer profile is needed.
 */
export function useCurrentUser(enabled: boolean) {
  return useQuery({
    queryKey: authKeys.currentUser,
    queryFn: async (): Promise<User> => {
      const user = userFromToken();
      if (!user) throw new Error('Could not read session.');
      return user;
    },
    enabled,
    staleTime: Infinity,
  });
}
